/**
 * Helpers de payload para chamadas a Edge Functions Supabase.
 *
 * CONTEXTO (bug C8 — 25/04/2026):
 *   Frontend enviava `tuition_plan_id: planId || null` para `convert-lead`.
 *   Edge Function rejeitava com 400 "Invalid request data" porque Zod
 *   `.optional()` nao aceita `null`. Solucao: payload externo deve ser
 *   canonico (campos opcionais OMITIDOS, nao enviados como `null`).
 *
 * CONTRATO (defense in depth):
 *   - Frontend STRICT: chamar `cleanPayload(body)` antes de `invoke()`.
 *   - Backend TOLERANTE: helpers em `supabase/functions/_shared/zod.ts`
 *     aceitam `null`/`undefined` por seguranca.
 *
 * REFERENCIA:
 *   - Plano: docs/superpowers/plans/2026-04-25-fix-definitivo-payload-validation.md
 */

/**
 * Remove keys com valor `null`, `undefined` ou string vazia (apos trim).
 * Preserva `false`, `0`, arrays vazios, objetos aninhados (shallow).
 *
 * Use ANTES de `supabase.functions.invoke({ body })`.
 *
 * @example
 *   const body = cleanPayload({
 *     mode: "student",
 *     lead_id,
 *     club_id,
 *     opportunity_id,
 *     tuition_plan_id: planId, // se null/undefined/"", removido
 *     court_id: courtId,
 *   });
 *   await supabase.functions.invoke("convert-lead", { body });
 */
export function cleanPayload<T extends Record<string, unknown>>(
  obj: T,
): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(obj) as Array<
    [keyof T, T[keyof T]]
  >) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    out[key] = value;
  }
  return out;
}

/**
 * Codigos de erro tipados retornados pelas Edge Functions PPS.
 *
 * MANTER EM SYNC com `supabase/functions/_shared/errors.ts` (`ErrorCode`).
 */
export type EdgeErrorCode =
  | "unauthorized"
  | "forbidden"
  | "invalid_request"
  | "rate_limited"
  | "not_found"
  | "conflict"
  | "rpc_error"
  | "external_service"
  | "credits_exhausted"
  | "missing_config"
  | "sem_asaas"
  | "sem_customer"
  | "asaas_indisponivel"
  | "not_allowed"
  | "terms_required"
  | "other_method_connected"
  | "gateway_unavailable"
  | "not_connecting"
  | "send_disabled"
  | "no_inbound_yet"
  | "internal_error";

const VALID_CODES: ReadonlySet<EdgeErrorCode> = new Set<EdgeErrorCode>([
  "unauthorized",
  "forbidden",
  "invalid_request",
  "rate_limited",
  "not_found",
  "conflict",
  "rpc_error",
  "external_service",
  "credits_exhausted",
  "missing_config",
  "sem_asaas",
  "sem_customer",
  "asaas_indisponivel",
  "not_allowed",
  "terms_required",
  "other_method_connected",
  "gateway_unavailable",
  "not_connecting",
  "send_disabled",
  "no_inbound_yet",
  "internal_error",
]);

function isEdgeErrorCode(s: string): s is EdgeErrorCode {
  return VALID_CODES.has(s as EdgeErrorCode);
}

/**
 * Extrai codigo de erro tipado de uma resposta de Edge Function.
 *
 * Lida com 2 formatos do supabase-js:
 *   - status >= 400 (FunctionsHttpError): err.context = Response, body = { error, details? }
 *   - status 200 com payload de erro: caller faz {data,error} = invoke(); error = null
 *     mas data = { error: "code" } — caller passa data para esta funcao.
 *
 * Sempre retorna `EdgeErrorCode` valido (fallback "internal_error" para
 * payloads desconhecidos / nao-PPS).
 *
 * @example
 *   try {
 *     const { data, error } = await supabase.functions.invoke("foo", { body });
 *     if (error) {
 *       const code = await extractEdgeError(error);
 *       toast.error(t(`errors.${code}`));
 *     }
 *   } catch (err) {
 *     const code = await extractEdgeError(err);
 *     toast.error(t(`errors.${code}`));
 *   }
 */
export async function extractEdgeError(err: unknown): Promise<EdgeErrorCode> {
  if (err === null || err === undefined) return "internal_error";

  // Caso 1: payload direto { error: "code" } (ex.: data de invoke())
  if (typeof err === "object" && err !== null && "error" in err) {
    const code = (err as { error: unknown }).error;
    if (typeof code === "string" && isEdgeErrorCode(code)) return code;
  }

  // Caso 2: FunctionsHttpError com Response em `context`
  const errObj = err as { context?: Response };
  if (errObj?.context && typeof errObj.context.json === "function") {
    try {
      const body = await errObj.context.json();
      const code = body?.error;
      if (typeof code === "string" && isEdgeErrorCode(code)) return code;
    } catch {
      // body nao-JSON ou ja consumido — cai no fallback
    }
  }

  return "internal_error";
}
